import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { ArrowUpRight, Menu, Mail, X } from "lucide-react";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "./ui/sheet";
import { Lockup, Star } from "./brand/Logo";
import { useLang } from "../i18n/LangContext";
import { EMAIL, EMAIL_HREF } from "../i18n/strings";

const NAV = ["work", "services", "studio", "contact"];

export const LangSwitch = ({ testid = "lang-switch" }) => {
  const { lang, switchLang, t } = useLang();
  return (
    <div className="label flex items-center gap-1 rounded-full border border-white/15 p-1" role="group" aria-label={t.a11y.langSwitch} data-testid={testid}>
      {["en", "es"].map((l) => (
        <button
          key={l}
          type="button"
          onClick={() => l !== lang && switchLang(l)}
          aria-pressed={l === lang}
          data-testid={`${testid}-${l}`}
          className={`rounded-full px-3 py-1.5 transition-colors duration-300 ${l === lang ? "bg-ivory text-ink" : "text-mist hover:text-ivory"}`}
        >
          {l.toUpperCase()}
        </button>
      ))}
    </div>
  );
};

export const Header = () => {
  const { t, path } = useLang();
  const [open, setOpen] = useState(false);
  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-white/5 bg-ink/[0.97]" data-testid="site-header">
      <div className="mx-auto flex h-20 max-w-[1440px] items-center justify-between px-5 md:px-10">
        <Link to={path("home")} className="block w-[150px] text-ivory md:w-[170px]" data-testid="header-logo-link" aria-label={t.a11y.logo}>
          <Lockup className="w-full" />
        </Link>
        <nav className="hidden items-center gap-9 lg:flex" aria-label={t.a11y.mainNav}>
          {NAV.map((k) => (
            <NavLink key={k} to={path(k)} data-testid={`nav-${k}`}
              className={({ isActive }) => `link-star label transition-colors duration-300 ${isActive ? "text-ivory" : "text-mist hover:text-ivory"}`}>
              {t.nav[k]}
            </NavLink>
          ))}
        </nav>
        <div className="hidden items-center gap-4 lg:flex">
          <LangSwitch />
          <Link to={path("contact")} data-testid="header-cta-button"
            className="label group flex items-center gap-2 rounded-full border border-lav px-5 py-3 text-ivory transition-colors duration-300 hover:bg-lav hover:text-ink">
            {t.nav.cta}
            <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:rotate-45" />
          </Link>
        </div>
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <button className="text-ivory lg:hidden" aria-label={t.nav.menu} data-testid="mobile-menu-button">
              <Menu className="h-6 w-6" />
            </button>
          </SheetTrigger>
          <SheetContent side="right" className="w-full border-white/10 bg-ink p-0 text-ivory sm:max-w-md [&>button]:hidden">
            <SheetTitle className="sr-only">{t.nav.menu}</SheetTitle>
            <div className="flex h-20 items-center justify-between px-5">
              <LangSwitch testid="mobile-lang-switch" />
              <button onClick={() => setOpen(false)} aria-label={t.nav.close} data-testid="mobile-menu-close"><X className="h-6 w-6" /></button>
            </div>
            <nav className="flex flex-col gap-2 px-5 pt-8" aria-label={t.a11y.mobileNav}>
              {["home", ...NAV].map((k, i) => (
                <NavLink key={k} to={path(k)} end onClick={() => setOpen(false)} data-testid={`mobile-nav-${k}`}
                  className={({ isActive }) => `flex items-baseline gap-4 border-b border-white/10 py-4 font-display text-3xl font-semibold uppercase ${isActive ? "text-lav" : "text-ivory"}`}>
                  <span className="label text-mist">0{i + 1}</span>{t.nav[k]}
                </NavLink>
              ))}
            </nav>
            <div className="absolute inset-x-5 bottom-8 flex items-center justify-between">
              <a href={EMAIL_HREF} className="label flex items-center gap-2 text-ivory" data-testid="mobile-email-link"><Mail className="h-4 w-4 text-lav" />{EMAIL}</a>
              <Star className="h-6 w-6 text-lav" />
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
};
